import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FamilyMember, RelationType } from '../types';
import { 
  Edit2, Search, Info, Trash2, Heart, 
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, Ghost,
  Plus, Minus, Users, ArrowUpCircle, Baby, Target
} from 'lucide-react';

// --- INJECTED STYLES FOR BRIGHTER LINES ---
const treeStyles = `
.tree ul {
    padding-top: 20px; position: relative;
    transition: all 0.5s;
    display: flex; justify-content: center;
}
.tree li {
    float: left; text-align: center;
    list-style-type: none;
    position: relative;
    padding: 20px 5px 0 5px;
    transition: all 0.5s;
}
/* Connectors */
.tree li::before, .tree li::after {
    content: ''; position: absolute; top: 0; right: 50%;
    border-top: 2px solid #818cf8; /* Indigo-400 (Brighter) */
    width: 50%; height: 20px;
}
.tree li::after {
    right: auto; left: 50%;
    border-left: 2px solid #818cf8; 
}
.tree li:only-child::after, .tree li:only-child::before {
    display: none;
}
.tree li:only-child { padding-top: 0; }
.tree li:first-child::before, .tree li:last-child::after {
    border: 0 none;
}
.tree li:last-child::before{
    border-right: 2px solid #818cf8; 
    border-radius: 0 5px 0 0;
}
.tree li:first-child::after{
    border-radius: 5px 0 0 0;
}
.tree ul ul::before{
    content: ''; position: absolute; top: 0; left: 50%;
    border-left: 2px solid #818cf8; 
    width: 0; height: 20px;
}
`;

interface FamilyTreeProps {
  members: Record<string, FamilyMember>;
  currentUser: string;
  onAddMember: (relation: RelationType, relativeToId: string) => void;
  onEditMember: (memberId: string) => void;
  onDeleteMember: (memberId: string) => void;
  onInvite?: (memberId: string) => void; 
  initialFocusId?: string | null;
}

const FamilyTree: React.FC<FamilyTreeProps> = ({ members, currentUser, onAddMember, onEditMember, onDeleteMember, onInvite, initialFocusId }) => {
  const [focusId, setFocusId] = useState(initialFocusId || currentUser);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isCentering, setIsCentering] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showEditAid, setShowEditAid] = useState(true);
  const [showAddAid, setShowAddAid] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
      if (initialFocusId && members[initialFocusId]) {
          setFocusId(initialFocusId);
      }
  }, [initialFocusId, members]);

  // Safety Check
  useEffect(() => {
    if (!members[focusId]) {
      setFocusId(currentUser);
    }
  }, [members, focusId, currentUser]);

  // Center the tree by finding the "Me" node and positioning it in viewport center
  const centerView = useCallback(() => {
    setTimeout(() => {
      if (containerRef.current) {
        const meNode = containerRef.current.querySelector(`[data-node-id="${currentUser}"]`) as HTMLElement;
        
        if (meNode) {
          const rect = meNode.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();
          
          // We need to move the tree so the Me card appears at the center of the viewport
          // The position values are in screen space, so we calculate directly
          const screenCenterX = containerRect.width / 2;
          const screenCenterY = containerRect.height / 2;
          
          // Where is the Me card relative to the container?
          const cardXInContainer = rect.left - containerRect.left;
          const cardYInContainer = rect.top - containerRect.top;
          const cardCenterXInContainer = cardXInContainer + rect.width / 2;
          const cardCenterYInContainer = cardYInContainer + rect.height / 2;
          
          // How much do we need to move to center it?
          const offsetX = screenCenterX - cardCenterXInContainer;
          const offsetY = screenCenterY - cardCenterYInContainer;
          
          // Current position needs to be adjusted by these offsets
          setPosition(prev => ({ x: prev.x + offsetX, y: prev.y + offsetY }));
        }
      }
    }, 50);
  }, [currentUser]);

  // Run centering on component mount and when viewing different trees
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const meNode = containerRef.current.querySelector(`[data-node-id="${currentUser}"]`) as HTMLElement;
        
        if (meNode) {
          const rect = meNode.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();
          
          // Calculate the center of the Me card on screen
          const meCardCenterX = rect.left + rect.width / 2;
          const meCardCenterY = rect.top + rect.height / 2;
          
          // Calculate the center of the container
          const containerCenterX = containerRect.left + containerRect.width / 2;
          const containerCenterY = containerRect.top + containerRect.height / 2;
          
          // Calculate how much we need to move the tree to center the Me card
          // Account for scale because transform = translate(position) scale(scale)
          const moveX = (containerCenterX - meCardCenterX) / scale;
          const moveY = (containerCenterY - meCardCenterY) / scale;
          
          setPosition({ x: moveX, y: moveY });
        }
      }
    }, 200); // Longer delay to ensure DOM is fully rendered
    
    return () => clearTimeout(timer);
  }, [focusId, scale]); // Re-center when viewing different family members

  const getInitials = (name: string) => {
      return name
          .split(' ')
          .map(n => n[0])
          .join('')
          .substring(0, 2)
          .toUpperCase();
  };

  // --- FIX: LOCAL INVITE HANDLER ---
  const handleInviteAction = (member: FamilyMember) => {
      // 1. Check for Phone Number
      if (!member.phoneNumber || member.phoneNumber.length < 5) {
          const creatorName = members[member.createdBy]?.name || "the Admin";
          alert(`Please add a mobile number for ${member.name} first by clicking the Edit button.`);
          return;
      }

      // 2. Get the familyId from current user or the member being invited
      const inviterMember = members[currentUser];
      const familyId = inviterMember?.familyId || member.familyId;
      
      // 3. Create invite URL with familyId parameter
      const inviteUrl = `https://famitry.in?familyId=${encodeURIComponent(familyId)}`;
      
      // 4. Prepare Message
      const firstName = member.name.split(' ')[0];
      const message = `Hi ${firstName}! We are building our digital family legacy on Famitry and you are already in the tree! 🌳\n\nIt's amazing to see our family insights—like where everyone is living, what professions we are in, and our blood groups—all in one place.\n\nJoin in to see our roots and add other members you know: ${inviteUrl}\n\nWaiting for you! 👨‍👩‍👧‍👦`;
      
      // 5. Open WhatsApp
      window.open(`https://wa.me/${member.phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // --- RELATIONSHIP LOGIC ---
  const getRelationshipLabel = useCallback((targetId: string) => {
    if (targetId === currentUser) return 'Me';
    const target = members[targetId];
    const me = members[currentUser];
    if (!target || !me) return target?.relation || '';

    // Debug: Log the check
    console.log(`[RELATION CHECK] target=${target?.name}, me=${me?.name}, me.motherId=${me?.motherId}, me.fatherId=${me?.fatherId}, me.parentIds=${me?.parentIds}, targetId=${targetId}`);

    // Direct parent check (for immediate parents only) - HIGHEST PRIORITY
    if (me.fatherId === targetId) {
        console.log(`[MATCH] Direct Father match for ${target.name}`);
        return 'Father';
    }
    if (me.motherId === targetId) {
        console.log(`[MATCH] Direct Mother match for ${target.name}`);
        return 'Mother';
    }
    
    // Also check if target is in parentIds but not explicitly fatherId/motherId
    if (me.parentIds?.includes(targetId)) {
        if (!members[targetId]) {
            console.warn(`[RELATIONSHIP] Parent ID in array but member not found: ${targetId}`);
            return '';
        }
        console.log(`[MATCH] Target in parentIds for ${target.name}, gender=${target.gender}`);
        return target.gender === 'Female' ? 'Mother' : 'Father';
    }

    const getAncestorLevel = (startId: string, searchId: string, visited = new Set<string>()): number => {
        if (startId === searchId) return 0;
        if (visited.has(startId)) return -1;
        visited.add(startId);
        const member = members[startId];
        if (!member) return -1;
        for (const pid of member.parentIds || []) {
            const dist = getAncestorLevel(pid, searchId, new Set(visited));
            if (dist !== -1) return dist + 1;
        }
        return -1;
    };

    const getDescendantLevel = (startId: string, searchId: string, visited = new Set<string>()): number => {
        if (startId === searchId) return 0;
        if (visited.has(startId)) return -1;
        visited.add(startId);
        const member = members[startId];
        if (!member) return -1;
        for (const cid of member.childrenIds || []) {
            const dist = getDescendantLevel(cid, searchId, new Set(visited));
            if (dist !== -1) return dist + 1;
        }
        return -1;
    };

    const ancestorLevel = getAncestorLevel(currentUser, targetId);
    if (ancestorLevel > 0) {
        const isMale = target.gender === 'Male' || target.gender === 'male';
        if (ancestorLevel === 1) {
            const result = isMale ? 'Father' : 'Mother';
            console.log(`[DEBUG] ancestorLevel 1: target=${target.name}, gender=${target.gender}, isMale=${isMale}, result=${result}`);
            return result;
        }
        if (ancestorLevel === 2) {
            const result = (target.gender === 'Male' || target.gender === 'male') ? 'Grandfather' : 'Grandmother';
            console.log(`[DEBUG] ancestorLevel 2: target=${target.name}, gender=${target.gender}, result=${result}`);
            return result;
        }
        const greats = ancestorLevel - 2;
        const suffix = target.gender === 'Male' ? 'Grandfather' : 'Grandmother';
        if (greats < 3) return `${"Great-".repeat(greats)}${suffix}`;
        return `${greats}x Great-${suffix}`;
    }

    // Check if target is spouse of an ancestor
    const ancestorSpouseLevel = getAncestorLevel(currentUser, targetId);
    if (ancestorSpouseLevel === -1) {
        // Check if target is spouse of any ancestor
        const myAncestors = new Set<string>();
        const collectAncestors = (id: string, visited = new Set<string>()): void => {
            if (visited.has(id)) return;
            visited.add(id);
            const member = members[id];
            if (!member) return;
            for (const pid of member.parentIds || []) {
                myAncestors.add(pid);
                collectAncestors(pid, new Set(visited));
            }
        };
        collectAncestors(currentUser);

        for (const ancestorId of myAncestors) {
            const ancestor = members[ancestorId];
            if (!ancestor) continue;
            const ancestorSpouses = [ancestor.spouseId, ...(ancestor.spouseIds || [])].filter(Boolean) as string[];
            if (ancestorSpouses.includes(targetId)) {
                const ancestorLevel = getAncestorLevel(currentUser, ancestorId);
                if (ancestorLevel === 1) {
                    return target.gender === 'Male' ? 'Father' : 'Mother';
                }
                if (ancestorLevel === 2) {
                    return target.gender === 'Male' ? 'Grandfather' : 'Grandmother';
                }
                const greats = ancestorLevel - 2;
                const suffix = target.gender === 'Male' ? 'Grandfather' : 'Grandmother';
                if (greats < 3) return `${"Great-".repeat(greats)}${suffix}`;
                return `${greats}x Great-${suffix}`;
            }
        }
    }

    const descendantLevel = getDescendantLevel(currentUser, targetId);
    if (descendantLevel > 0) {
        if (descendantLevel === 1) return target.gender === 'Male' ? 'Son' : 'Daughter';
        if (descendantLevel === 2) return target.gender === 'Male' ? 'Grandson' : 'Granddaughter';
        const greats = descendantLevel - 2;
        const suffix = target.gender === 'Male' ? 'Grandson' : 'Granddaughter';
        if (greats < 3) return `${"Great-".repeat(greats)}${suffix}`;
        return `${greats}x Great-${suffix}`;
    }

    if (me.spouseId === targetId || me.spouseIds?.includes(targetId)) return target.gender === 'Male' ? 'Husband' : 'Wife';

    const shareParent = me.parentIds?.some(pid => target.parentIds?.includes(pid));
    if (shareParent) return target.gender === 'Male' ? 'Brother' : 'Sister';

    const isUncleAunt = me.parentIds?.some(pid => {
        const parent = members[pid];
        if (!parent) return false;
        return parent.parentIds?.some(grandPid => target.parentIds?.includes(grandPid)) && targetId !== pid;
    });
    if (isUncleAunt) return target.gender === 'Male' ? 'Uncle' : 'Aunt';

    const isGreatUncleAunt = me.parentIds?.some(pid => {
        const parent = members[pid];
        return parent?.parentIds?.some(gpid => {
             const grandParent = members[gpid];
             return grandParent?.parentIds?.some(ggpid => target.parentIds?.includes(ggpid)) && targetId !== gpid;
        });
    });
    if (isGreatUncleAunt) return target.gender === 'Male' ? 'Great-Uncle' : 'Great-Aunt';

    const isNephewNiece = target.parentIds?.some(pid => {
         return me.parentIds?.some(myPid => members[pid]?.parentIds?.includes(myPid)) && pid !== currentUser;
    });
    if (isNephewNiece) return target.gender === 'Male' ? 'Nephew' : 'Niece';
    
    if (me.spouseId || (me.spouseIds && me.spouseIds.length > 0)) {
         const spouses = [me.spouseId, ...(me.spouseIds || [])].filter(Boolean) as string[];
         for (const sid of spouses) {
             const spouse = members[sid];
             if (!spouse) continue;
             if (spouse.parentIds?.includes(targetId)) return target.gender === 'Male' ? 'Father-in-law' : 'Mother-in-law';
             const isSpouseSibling = spouse.parentIds?.some(pid => target.parentIds?.includes(pid));
             if (isSpouseSibling) return target.gender === 'Male' ? 'Brother-in-law' : 'Sister-in-law';
         }
    }
    
    const isSiblingSpouse = (target.spouseId || (target.spouseIds && target.spouseIds.length > 0)) && me.parentIds?.some(pid => {
        const spouses = [target.spouseId, ...(target.spouseIds || [])].filter(Boolean) as string[];
        return spouses.some(sid => members[sid]?.parentIds?.includes(pid));
    });
    if (isSiblingSpouse) return target.gender === 'Male' ? 'Brother-in-law' : 'Sister-in-law';

    const isCoSibling = (me.spouseId || (me.spouseIds && me.spouseIds.length > 0)) && (target.spouseId || (target.spouseIds && target.spouseIds.length > 0)) && (() => {
        const mySpouses = [me.spouseId, ...(me.spouseIds || [])].filter(Boolean) as string[];
        const targetSpouses = [target.spouseId, ...(target.spouseIds || [])].filter(Boolean) as string[];
        return mySpouses.some(mySid => {
             const mySpouse = members[mySid];
             return targetSpouses.some(tSid => {
                 const targetSpouse = members[tSid];
                 return mySpouse?.parentIds?.some(pid => targetSpouse?.parentIds?.includes(pid));
             });
        });
    })();

    if (isCoSibling) return target.gender === 'Male' ? 'Co-Brother' : 'Co-Sister';

    const isSpouseSiblingChild = me.spouseId && target.parentIds?.some(pid => {
        const parent = members[pid];
        const mySpouse = members[me.spouseId];
        return parent && mySpouse && mySpouse.parentIds?.some(gp => parent.parentIds?.includes(gp));
    });
    if (isSpouseSiblingChild) return target.gender === 'Male' ? 'Nephew' : 'Niece';

    const isSpouseParentSibling = me.spouseId && target.parentIds?.some(gp => {
        const mySpouse = members[me.spouseId];
        return mySpouse?.parentIds?.some(pid => {
             const spouseParent = members[pid];
             return spouseParent?.parentIds?.includes(gp);
        });
    });
    if (isSpouseParentSibling) return target.gender === 'Male' ? 'Uncle' : 'Aunt';

    const isUncle = (target.spouseId || (target.spouseIds && target.spouseIds.length > 0)) && me.parentIds?.some(pid => {
        const spouses = [target.spouseId, ...(target.spouseIds || [])].filter(Boolean) as string[];
        return spouses.some(sid => members[sid]?.parentIds?.includes(pid));
    });
    if (isUncle) return target.gender === 'Male' ? 'Uncle' : 'Aunt';
    
    // Fallback: return the stored relation or empty
    return target.relation || '';
  }, [members, currentUser]);

  const filteredMembers = (Object.values(members) as FamilyMember[]).filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchSelect = (id: string) => {
    setFocusId(id);
    setSearchTerm('');
    setShowSearchResults(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('.node-card')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('.node-card')) return;
    setIsDragging(true);
    const touch = e.touches[0];
    setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      const touch = e.touches[0];
      setPosition({ x: touch.clientX - dragStart.x, y: touch.clientY - dragStart.y });
    }
  };

  const handleZoom = useCallback((delta: number) => {
    setIsCentering(true);
    setScale(prev => {
      const newScale = Math.min(Math.max(0.2, prev + delta), 2.5);
      
      // Immediately recalculate position for new scale to prevent jumping
      const activeNode = document.querySelector(`[data-node-id="${focusId}"]`);
      if (activeNode && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const nodeRect = activeNode.getBoundingClientRect();
        const screenCenterX = containerRect.width / 2;
        const screenCenterY = containerRect.height / 2;
        const nodeCenterX = nodeRect.left + nodeRect.width / 2;
        const nodeCenterY = nodeRect.top + nodeRect.height / 2;
        
        // Adjust position for NEW scale to keep node centered
        const moveX = (screenCenterX - nodeCenterX) / newScale;
        const moveY = (screenCenterY - nodeCenterY) / newScale;
        
        setPosition({ x: moveX, y: moveY });
      }
      
      setTimeout(() => setIsCentering(false), 300);
      return newScale;
    });
  }, [focusId]);

  const resetView = useCallback(() => {
    setIsCentering(true);
    const activeNode = document.querySelector(`[data-node-id="${focusId}"]`);
    if (activeNode && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const nodeRect = activeNode.getBoundingClientRect();
        const screenCenterX = containerRect.width / 2;
        const screenCenterY = containerRect.height / 2;
        const nodeCenterX = nodeRect.left + nodeRect.width / 2;
        const nodeCenterY = nodeRect.top + nodeRect.height / 2;
        setPosition(prev => ({ 
            x: prev.x + (screenCenterX - nodeCenterX), 
            y: prev.y + (screenCenterY - nodeCenterY) 
        }));
    } else {
        setPosition({ x: 0, y: 0 });
        setScale(1);
    }
    setTimeout(() => setIsCentering(false), 500);
  }, [focusId]);

  useEffect(() => {
    const timeout = setTimeout(resetView, 150);
    return () => clearTimeout(timeout);
  }, [focusId, resetView]);

  const pan = (direction: 'up' | 'down' | 'left' | 'right') => {
    const step = 150 / scale;
    setPosition(prev => {
        switch(direction) {
            case 'up': return { ...prev, y: prev.y + step };
            case 'down': return { ...prev, y: prev.y - step };
            case 'left': return { ...prev, x: prev.x + step };
            case 'right': return { ...prev, x: prev.x - step };
            default: return prev;
        }
    });
  };

  const getRoots = (startId: string) => {
    let current = members[startId];
    if (!current) {
        console.warn(`[GET ROOTS] Start member not found: ${startId}`);
        return [];
    }
    let depth = 0;
    const visited = new Set<string>();
    
    const isMarried = current.maritalStatus === 'Married' || current.isMarried;
    if ((!current.parentIds || current.parentIds.length === 0) && isMarried) {
        const spouseIds = current.spouseIds || (current.spouseId ? [current.spouseId] : []);
        const spouseWithParents = spouseIds.find(sid => members[sid]?.parentIds && members[sid].parentIds.length > 0);
        if (spouseWithParents) {
            current = members[spouseWithParents];
            console.log(`[GET ROOTS] Switched to spouse with parents: ${current.name}`);
        }
    }

    while (current && current.parentIds && current.parentIds.length > 0 && depth < 25 && !visited.has(current.id)) {
        visited.add(current.id);
        // Filter out invalid parent IDs
        const validParentIds = current.parentIds.filter(pid => members[pid]);
        if (validParentIds.length === 0) {
            console.warn(`[GET ROOTS] No valid parents found for ${current.name}, stopping`);
            break;
        }
        const fatherId = validParentIds.find(pid => members[pid]?.gender === 'Male');
        const parentId = fatherId || validParentIds[0];
        if (members[parentId]) {
            current = members[parentId];
        } else {
            console.warn(`[GET ROOTS] Parent not found: ${parentId}`);
            break;
        }
        depth++;
    }
    return [current];
  };
  
  // Always show the tree, even if user is alone - use currentUser as root if no roots found
  const currentRoots = getRoots(focusId);
  const displayRoots = currentRoots.length > 0 ? currentRoots : (members[currentUser] ? [members[currentUser]] : []);

  // --- CARD COMPONENT ---
  const FamilyNodeCard: React.FC<{ member: FamilyMember }> = ({ member }) => {
    const isFocus = member.id === focusId;
    const isCurrentUser = member.id === currentUser;
    const canEdit = isCurrentUser || !member.joined;
    const canDelete = member.createdBy === currentUser && member.id !== currentUser;
    const isDeceased = !!member.isDeceased;
    const relationLabel = getRelationshipLabel(member.id);

    const isFemale = member.gender === 'Female';
    const headerColor = isDeceased ? 'bg-slate-200' : (isFemale ? 'bg-pink-200 border-pink-300' : 'bg-blue-200 border-blue-300');
    const borderColor = isFocus ? 'border-indigo-600 ring-4 ring-indigo-50/50' : (isFemale ? 'border-pink-300' : 'border-blue-300');
    const avatarBorder = isFemale ? 'border-pink-200' : 'border-blue-200';
    const initials = getInitials(member.name);

    const [showMenu, setShowMenu] = useState(false);

    return (
        <div 
            data-node-id={member.id}
            className={`
                node-card group relative flex flex-col items-center bg-white rounded-2xl shadow-xl border-[3px]
                transition-all duration-300 w-40 overflow-visible cursor-pointer
                ${borderColor}
                ${isDeceased ? 'grayscale' : ''}
                ${isFocus ? 'z-20 scale-105' : ''}
            `}
            onClick={(e) => { e.stopPropagation(); setFocusId(member.id); }}
            onMouseLeave={() => setShowMenu(false)}
        >
            <div className={`w-full h-12 ${headerColor} border-b-2 border-inherit relative rounded-t-xl`}>
                {isCurrentUser && <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow-sm z-10">ME</div>}
                
                {isDeceased && <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[7px] font-black px-1.5 py-0.5 rounded shadow-sm flex items-center gap-1"><Ghost className="w-2 h-2"/> LATE</div>}
                
                {/* --- LEFT ACTION MENU --- */}
                <div className="absolute top-1 left-1 z-30">
                    <button 
                        onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                        className={`p-1 rounded-full transition-colors shadow-sm ${showMenu ? 'bg-white text-indigo-700' : 'bg-white/50 hover:bg-white text-indigo-700'}`}
                    >
                        {showMenu ? <X className="w-3.5 h-3.5"/> : <Plus className="w-3.5 h-3.5" />}
                    </button>
                    
                    {showMenu && (
                        <div className="absolute top-full left-0 mt-1 flex flex-col gap-1 bg-white/95 backdrop-blur-sm p-1.5 rounded-lg shadow-xl border border-indigo-100 animate-in fade-in zoom-in-95 origin-top-left w-24 z-50">
                            <button onClick={(e) => { e.stopPropagation(); onAddMember(RelationType.CHILD, member.id); setShowMenu(false); }} className="flex items-center gap-2 px-2 py-1.5 hover:bg-indigo-50 rounded text-[10px] font-bold text-gray-700 hover:text-indigo-600 text-left"><Baby className="w-3 h-3" /> Child</button>
                            <button onClick={(e) => { e.stopPropagation(); onAddMember(RelationType.SPOUSE, member.id); setShowMenu(false); }} className="flex items-center gap-2 px-2 py-1.5 hover:bg-pink-50 rounded text-[10px] font-bold text-gray-700 hover:text-pink-600 text-left"><Heart className="w-3 h-3" /> Spouse</button>
                            <button onClick={(e) => { e.stopPropagation(); onAddMember(RelationType.FATHER, member.id); setShowMenu(false); }} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded text-[10px] font-bold text-gray-700 hover:text-slate-600 text-left"><ArrowUpCircle className="w-3 h-3" /> Parent</button>
                        </div>
                    )}
                </div>

                {/* EDIT BUTTON (Right Side) - Delete moved to bottom */}
                {canEdit && (
                    <div className="absolute top-1 right-1 p-0.5 bg-red/50 hover:bg-red/80 rounded shadow-sm flex gap-1 z-30 transition-colors">
                        <button onClick={(e) => { e.stopPropagation(); onEditMember(member.id); }} className="p-1 rounded text-indigo-700 hover:bg-indigo-50" title="Edit"><Edit2 className="w-3 h-3" /></button>
                    </div>
                )}
            </div>
            
            {/* AVATAR OR INITIALS */}
            <div className="-mt-8 mb-2 relative z-10">
                {member.avatarUrl ? (
                    <img src={member.avatarUrl} className={`w-20 h-20 rounded-full object-cover border-[5px] shadow-md bg-white ${avatarBorder}`} />
                ) : (
                    <div className={`w-20 h-20 rounded-full border-[5px] shadow-md flex items-center justify-center text-2xl font-black tracking-tighter ${avatarBorder} ${isFemale ? 'bg-pink-50 text-pink-500' : 'bg-blue-50 text-blue-500'}`}>
                        {initials}
                    </div>
                )}
                
                {/* FIX: INVITE BUTTON WITH NEW HANDLER */}
                {!member.joined && !isDeceased && (
                    <button onClick={(e) => { e.stopPropagation(); handleInviteAction(member); }} className="absolute bottom-0 right-0 bg-amber-400 hover:bg-amber-500 text-white text-[8px] font-black px-2 py-0.5 rounded-full shadow border-2 border-white transition-colors">INVITE</button>
                )}
            </div>

            <div className="px-3 pb-4 text-center w-full">
                <h3 className="font-bold text-gray-900 truncate text-sm leading-tight">{member.name}</h3>
                {relationLabel && <p className="text-xs text-indigo-500 font-bold uppercase mt-0.5 tracking-wide truncate" title={relationLabel}>{relationLabel}</p>}
            </div>

            {/* RESTORED: DELETE BUTTON TO BOTTOM LEFT (z-50, bg-white, shadow) */}
            
            {canDelete && (
    <button 
        onClick={(e) => { 
            e.stopPropagation(); 
            // 1. ADDED WARNING: This ensures the user is asked before deleting
            if (window.confirm(`Are you sure you want to delete ${member.name}? This action cannot be undone.`)) {
                onDeleteMember(member.id); 
            }
        }} 
        // 2. STYLING FIXES:
        // - Added 'bg-white shadow-md border border-red-100' so the button pops out visually
        // - Increased z-index to 50 to ensure it sits on top of everything
        className="absolute bottom-1 left-1 p-1.5 text-red-300 hover:text-red-500 transition-colors z-20"
        title="Delete Member"
    >
        <Trash2 className="w-3.5 h-3.5" />
    </button>
)}
        </div>
    );
  };

  const TreeNode: React.FC<{ memberId: string }> = ({ memberId }) => {
      const member = members[memberId];
      if (!member) {
          console.warn(`[TREE NODE] Member not found: ${memberId}`);
          return null;
      }

      const spouseIds = member.spouseIds || (member.spouseId ? [member.spouseId] : []);
      const spouses = spouseIds.map(id => members[id]).filter(Boolean) as FamilyMember[];

      const allChildrenSet = new Set<string>();
      (member.childrenIds || []).forEach(id => {
          if (members[id]) allChildrenSet.add(id);
          else console.warn(`[TREE NODE] Child not found: ${id} for parent ${member.name}`);
      });
      spouses.forEach(s => {
          (s.childrenIds || []).forEach(id => {
              if (members[id]) allChildrenSet.add(id);
              else console.warn(`[TREE NODE] Child not found: ${id} for spouse ${s.name}`);
          });
      });
      const allChildren = Array.from(allChildrenSet);

      // Separate spouses into left (odd: 1st, 3rd, 5th...) and right (even: 2nd, 4th, 6th...) positions
      const leftSpouses = spouses.filter((_: any, index: number) => index % 2 === 0);
      const rightSpouses = spouses.filter((_: any, index: number) => index % 2 === 1);

      return (
          <li>
              <div className="tree-node-content">
                  <div className="flex items-center gap-8 p-3 bg-white/40 rounded-3xl border-2 border-slate-300/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow duration-300">
                        {/* LEFT SPOUSES (1st, 3rd, 5th...) - Reversed so closest is to left of primary */}
                        {leftSpouses.slice().reverse().map(spouse => (
                            <React.Fragment key={spouse.id}>
                                <FamilyNodeCard member={spouse} />
                                <div className="flex flex-col items-center justify-center -mx-4 z-0">
                                    <div className="w-10 h-1 bg-pink-300/50 rounded-full"></div>
                                    <Heart className="w-6 h-6 text-pink-500 fill-pink-200 drop-shadow-sm -mt-3.5 z-10" />
                                </div>
                            </React.Fragment>
                        ))}

                        {/* PRIMARY MEMBER */}
                        <FamilyNodeCard member={member} />
                        
                        {/* RIGHT SPOUSES (2nd, 4th, 6th...) */}
                        {rightSpouses.map(spouse => (
                            <React.Fragment key={spouse.id}>
                                <div className="flex flex-col items-center justify-center -mx-4 z-0">
                                    <div className="w-10 h-1 bg-pink-300/50 rounded-full"></div>
                                    <Heart className="w-6 h-6 text-pink-500 fill-pink-200 drop-shadow-sm -mt-3.5 z-10" />
                                </div>
                                <FamilyNodeCard member={spouse} />
                            </React.Fragment>
                        ))}
                  </div>
              </div>
              
              {allChildren.length > 0 && (
                  <ul>
                      {allChildren.map(cid => (
                          <TreeNode key={cid} memberId={cid} />
                      ))}
                  </ul>
              )}
          </li>
      );
  };

  // Show onboarding aids if only 1 member (the current user) - but still show the tree
  const memberList = Object.values(members);
  const showAids = memberList.length === 1 && memberList[0].id === currentUser;
  const currentUserObj = members[currentUser];

  return (
    <div 
        className="absolute inset-0 w-full h-full relative bg-[#fdfbf7] overflow-hidden select-none flex flex-col touch-none md:static md:h-screen md:w-full"
        style={{ touchAction: 'none', backgroundImage: 'radial-gradient(#d6d3d1 1px, transparent 1px)', backgroundSize: '24px 24px' }}
    >
        <style>{treeStyles}</style>

                // ...existing code...

        {/* MOBILE SEARCH */}
        <div className="absolute top-4 left-4 right-4 md:left-72 md:right-auto md:w-80 z-[60] pointer-events-none">
             <div className="mb-2 pointer-events-auto bg-indigo-600/90 backdrop-blur-md text-white px-2 md:px-3 py-1 md:py-2 rounded-xl text-[9px] md:text-[10px] font-bold shadow-lg border border-indigo-400/30 flex items-center gap-2 animate-fade-in w-fit">
                <div className="bg-white/20 rounded-full p-0.5 md:p-1"><Info className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" /></div>
                <span className="hidden md:inline">Click on any card to view their tree</span>
                <span className="md:hidden">Click cards to view</span>
             </div>

             <div className="relative pointer-events-auto bg-white/60 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 flex items-center px-3 md:px-4 py-2 md:py-3">
                 <Search className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-500 mr-2" />
                 <input 
                    type="text" 
                    placeholder="Search..." 
                    className="text-xs md:text-sm bg-transparent outline-none w-full text-gray-900 font-bold placeholder:text-gray-500" 
                    value={searchTerm} 
                    onChange={(e) => { setSearchTerm(e.target.value); setShowSearchResults(true); }} 
                    onFocus={() => setShowSearchResults(true)}
                 />
                 {searchTerm && <button onClick={() => setSearchTerm('')} className="p-1 hover:bg-white/40 rounded-full"><X className="w-3 h-3 md:w-4 md:h-4 text-gray-500" /></button>}
             </div>
             {showSearchResults && searchTerm && (
                 <div className="absolute top-full left-0 right-0 mt-2 bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 max-h-64 overflow-y-auto z-[60] pointer-events-auto no-scrollbar">
                    {filteredMembers.length > 0 ? (
                        filteredMembers.map(m => (
                            <button key={m.id} className="w-full text-left px-3 md:px-4 py-2 md:py-3 hover:bg-indigo-50/50 text-xs flex items-center gap-2 md:gap-3 transition-colors border-b border-gray-100 last:border-0" onClick={() => handleSearchSelect(m.id)}>
                                <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full border-2 flex items-center justify-center font-bold text-[9px] md:text-[10px] ${m.gender === 'Female' ? 'bg-pink-50 text-pink-600 border-pink-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>{getInitials(m.name)}</div>
                                <div><p className="font-black text-gray-900 text-xs md:text-sm">{m.name}</p><p className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase">{getRelationshipLabel(m.id)}</p></div>
                            </button>
                        ))
                    ) : <div className="p-4 text-center text-gray-400 text-xs font-bold">No results found</div>}
                 </div>
             )}
        </div>

        {/* Controls */}
        <div className="absolute bottom-24 md:bottom-10 left-6 z-[70] grid grid-cols-3 gap-1 p-2 bg-transparent">
            <div />
            <button onClick={() => pan('up')} className="w-10 h-10 bg-white/40 backdrop-blur-sm rounded-xl flex items-center justify-center text-indigo-700 hover:bg-white/60 active:scale-90 transition-all shadow-sm"><ChevronUp className="w-5 h-5" /></button>
            <div />
            <button onClick={() => pan('left')} className="w-10 h-10 bg-white/40 backdrop-blur-sm rounded-xl flex items-center justify-center text-indigo-700 hover:bg-white/60 active:scale-90 transition-all shadow-sm"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={resetView} className="w-10 h-10 bg-indigo-600/40 backdrop-blur-sm rounded-xl flex items-center justify-center text-white hover:bg-indigo-600/60 active:scale-90 transition-all shadow-sm"><Target className="w-4 h-4" /></button>
            <button onClick={() => pan('right')} className="w-10 h-10 bg-white/40 backdrop-blur-sm rounded-xl flex items-center justify-center text-indigo-700 hover:bg-white/60 active:scale-90 transition-all shadow-sm"><ChevronRight className="w-5 h-5" /></button>
            <div />
            <button onClick={() => pan('down')} className="w-10 h-10 bg-white/40 backdrop-blur-sm rounded-xl flex items-center justify-center text-indigo-700 hover:bg-white/60 active:scale-90 transition-all shadow-sm"><ChevronDown className="w-5 h-5" /></button>
            <div />
        </div>

        <div className="fixed bottom-24 md:bottom-10 right-6 z-[70] flex flex-col gap-3 bg-transparent">
            <button onClick={() => handleZoom(0.2)} className="w-14 h-14 bg-white/40 backdrop-blur-md hover:bg-white/60 rounded-full text-indigo-700 border border-white/20 transition-all flex items-center justify-center active:scale-90 shadow-xl"><Plus className="w-6 h-6" /></button>
            <button onClick={() => handleZoom(-0.2)} className="w-14 h-14 bg-white/40 backdrop-blur-md hover:bg-white/60 rounded-full text-indigo-700 border border-white/20 transition-all flex items-center justify-center active:scale-90 shadow-xl"><Minus className="w-6 h-6" /></button>
        </div>

        <div 
            ref={containerRef}
            className={`w-full h-full cursor-grab ${isDragging ? 'cursor-grabbing' : ''}`}
            onMouseDown={handleMouseDown} 
            onMouseMove={handleMouseMove} 
            onMouseUp={handleMouseUp} 
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUp}
            onWheel={(e) => { if (e.ctrlKey) handleZoom(e.deltaY > 0 ? -0.1 : 0.1); }}
        >
            <div 
                className={`tree-container origin-center select-none ${isCentering ? 'transition-all duration-500 ease-out' : ''}`}
                style={{ transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`, willChange: isDragging ? 'transform' : 'auto' }}
            >
                <div className="tree">
                    <ul>
                        {displayRoots.map(root => (
                            <TreeNode key={root.id} memberId={root.id} />
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    </div>
  );
};

export default FamilyTree;